import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import * as yup from 'yup';
import AdapterDayjs from '@mui/lab/AdapterDayjs';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppDispatch } from '../../app/hooks';
import { useGetCareTeamQuery } from '../../app/services/account';
import {
  useAddTimeTrackerMutation,
  useEditTimeTrackerMutation,
  useGetTimeTrackerAssessmentsByPatientQuery,
} from '../../app/services/interview';
import { useGetPatientQuery } from '../../app/services/patient';
import { useToast } from '../../app/toast';
import CommonDatePicker from '../../common/components/CommonDatePicker';
import { LoadingOverlay } from '../../common/components/LoadingOverlay';
import RoundedContainer from '../../common/components/RoundedContainer';
import { PageStatus, setPageStatus } from '../../common/slices/globalSlice';
import { formatDate } from '../../common/utils/dateTimeUtils';
import {
  getCareTeamOptionLabel,
  getCareTeamValue,
  getTimeTrackerAssessmentsOptionLabel,
  getTimeTrackerAssessmentValue,
} from '../../common/utils/patientUtils';
import { TimeTrackerAssessmentType, TypesOfService } from '../../common/utils/timeTracker';
import { CareTeamItem } from '../../types/CareTeam';
import { CareTeamType } from '../../types/PatientRequest';
import { ResponseError } from '../../types/ResponseError';
import {
  TimeTrackerAddRequest,
  TimeTrackerAssessmentItem,
  TimeTrackerItem,
  TimeTrackerResponseItem,
  TimeTrackerUpdateRequest,
} from '../../types/TimeTracker';

interface TimeTrackerFormProps {
  timeTracker?: TimeTrackerResponseItem;
  patientId?: string;
  onTimeTrackerFormSubmitCallback: (result: boolean) => void;
}

export default function TimeTrackerForm({
  timeTracker,
  patientId,
  onTimeTrackerFormSubmitCallback,
}: TimeTrackerFormProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const toast = useToast();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const dispatch = useAppDispatch();
  dispatch(setPageStatus({ pageStatus: PageStatus.Idle }));
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [careTeamMember, setCareTeamMember] = useState<CareTeamItem[]>([]);
  const [timeTrackerAssessments, setTimeTrackerAssessments] = useState<TimeTrackerAssessmentItem[]>(
    []
  );
  const [addTimeTracker] = useAddTimeTrackerMutation();
  const [editTimeTracker] = useEditTimeTrackerMutation();
  const isEditing = !!timeTracker?.id;
  patientId = isEditing ? timeTracker.patientId : patientId;
  const { data: patientInfo } = useGetPatientQuery(
    patientId
      ? {
          patientId,
        }
      : skipToken
  );
  const { data: careTeamData } = useGetCareTeamQuery(
    patientInfo?.clinicAccountId ? { clinicAccountId: patientInfo.clinicAccountId } : skipToken
  );

  const { data: timeTrackerAssessmentData } = useGetTimeTrackerAssessmentsByPatientQuery(
    patientId
      ? {
          patientId,
        }
      : skipToken
  );

  const defaultFormValues = {
    patientId: '',
    dateOfService: new Date().toDateString(),
    typeOfServiceId: 0,
    minutes: undefined,
    measurement: '',
    interviewResultId: 0,
    trackerResultId: 0,
    careTeamMember: '',
    loginUserId: 0,
    contactId: 0,
  } as TimeTrackerItem;

  const validationSchema = yup
    .object({
      typeOfServiceId: yup
        .number()
        .typeError(t('timeTracker.error.blankTypeOfService'))
        .required(t('timeTracker.error.blankTypeOfService'))
        .moreThan(0, t('timeTracker.error.blankTypeOfService')),
      careTeamMember: yup.string().required(t('timeTracker.error.blankCareTeamMember')),
      minutes: yup
        .number()
        .typeError(t('timeTracker.error.blankMinutes'))
        .required(t('timeTracker.error.blankMinutes'))
        .integer(t('timeTracker.error.invalidMinuteValueType'))
        .moreThan(0, t('timeTracker.error.invalidMinutes')),
    })
    .required();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm<TimeTrackerItem>({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (patientInfo && careTeamData?.careTeam && timeTrackerAssessmentData?.assessmentItems) {
      const assignedCareTeam = patientInfo.careTeam || [];
      const careTeamInAccount = careTeamData?.careTeam || [];
      const patientCareTeam = new Set(assignedCareTeam.map((x) => `${x.id}-${x.type}`));

      const fullPatientCareTeam = careTeamInAccount.filter((item) =>
        patientCareTeam.has(`${item.id}-${item.type}`)
      );

      setCareTeamMember(fullPatientCareTeam || []);

      setTimeTrackerAssessments(timeTrackerAssessmentData?.assessmentItems || []);
      setIsLoading(false);
    }
  }, [patientInfo, careTeamData, timeTrackerAssessmentData]);

  useEffect(() => {
    if (
      patientInfo &&
      careTeamData?.careTeam &&
      timeTrackerAssessmentData?.assessmentItems &&
      timeTracker
    ) {
      if (isEditing && !isDataLoaded) {
        setIsDataLoaded(true);
        let timeTrackerData = {
          ...timeTracker,
          measurement: timeTracker.interviewResultId
            ? `${timeTracker.interviewResultId}-${TimeTrackerAssessmentType.Interview}`
            : timeTracker.trackerResultId
            ? `${timeTracker.trackerResultId}-${TimeTrackerAssessmentType.Monitor}`
            : null,
          careTeamMember: '',
        };

        // Check if care team member is assigned to the patient's care team
        const assignedCareTeam = patientInfo.careTeam || [];
        const patientCareTeam = new Set(assignedCareTeam.map((x) => `${x.id}-${x.type}`));
        let isValidCareTeam;
        let careTeamMember;
        if (timeTracker.loginUserId) {
          careTeamMember = `${timeTracker.loginUserId}-${CareTeamType.User}`;
        } else {
          careTeamMember = `${timeTracker.contactId}-${CareTeamType.Contact}`;
        }
        isValidCareTeam = patientCareTeam.has(careTeamMember);
        if (isValidCareTeam) {
          timeTrackerData = { ...timeTrackerData, careTeamMember };
        }

        Object.keys(timeTrackerData).forEach((attr) => {
          if (attr !== 'id') {
            setValue(attr as any, (timeTrackerData as any)[attr] ?? null);
          }
        });
      }
      setIsLoading(false);
    }
  }, [
    patientInfo,
    careTeamData,
    timeTrackerAssessments,
    timeTrackerAssessmentData,
    timeTracker,
    isDataLoaded,
    setValue,
    isEditing,
  ]);

  const onSubmit = async (timeTrackerItem: TimeTrackerItem) => {
    setDisableSubmit(true);
    setIsLoading(true);
    try {
      let submitData;
      const [careTeamMemberId, careTeamMemberType] =
        timeTrackerItem.careTeamMember?.split('-') ?? [];
      const loginUserId =
        Number(careTeamMemberType) === CareTeamType.User ? Number(careTeamMemberId) : undefined;
      const contactId =
        Number(careTeamMemberType) === CareTeamType.Contact ? Number(careTeamMemberId) : undefined;
      const [measurementId, measurementType] = timeTrackerItem.measurement?.split('-') ?? [];
      const interviewResultId =
        Number(measurementType) === TimeTrackerAssessmentType.Interview
          ? Number(measurementId)
          : undefined;
      const trackerResultId =
        Number(measurementType) === TimeTrackerAssessmentType.Monitor
          ? Number(measurementId)
          : undefined;
      if (isEditing) {
        submitData = {
          id: timeTracker.id,
          dateOfService: timeTrackerItem.dateOfService
            ? formatDate(timeTrackerItem.dateOfService)
            : '',
          minutes: timeTrackerItem.minutes,
          typeOfServiceId: timeTrackerItem.typeOfServiceId,
          loginUserId,
          contactId,
          interviewResultId,
          trackerResultId,
        } as TimeTrackerUpdateRequest;
        await editTimeTracker(submitData).unwrap();
      } else {
        submitData = {
          patientId,
          dateOfService: timeTrackerItem.dateOfService
            ? formatDate(timeTrackerItem.dateOfService)
            : '',
          minutes: timeTrackerItem.minutes,
          typeOfServiceId: timeTrackerItem.typeOfServiceId,
          loginUserId,
          contactId,
          interviewResultId,
          trackerResultId,
        } as TimeTrackerAddRequest;
        const addingRes = await addTimeTracker(submitData).unwrap();
        if (addingRes) {
          submitData = addingRes;
        }
      }

      onTimeTrackerFormSubmitCallback(true);

      setPageErrors([]);
      setIsLoading(false);

      toast.publish(
        (isEditing && t('timeTracker.updateSuccess')) || t('timeTracker.addSuccess'),
        'success'
      );
    } catch (e) {
      const {
        data: { error },
      } = e as ResponseError;
      setPageErrors(error);
      setIsLoading(false);
    }
    setDisableSubmit(false);
  };

  const handleTypeOfServiceChange = (e: any, values: any) => {
    const selectedValue = values ? values.id : undefined;
    setValue('typeOfServiceId', selectedValue);
  };
  const handleCareTeamMemberChange = (e: any, values: any) => {
    const selectedValue = values ? getCareTeamValue(values) : undefined;
    setValue('careTeamMember', selectedValue);
  };
  const handleMeasurementChange = (e: any, values: any) => {
    const selectedValue = values ? getTimeTrackerAssessmentValue(values) : undefined;
    setValue('measurement', selectedValue);
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <RoundedContainer sx={{ marginTop: 2, paddingTop: 1, paddingBottom: 3 }}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              px: isMobile ? 0 : 3,
              pt: 0,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <CommonDatePicker
                name="dateOfService"
                control={control}
                error={!!errors.dateOfService}
                helperText={errors.dateOfService?.message || ''}
                label={t('timeTracker.items.dateOfService')}
                matches={isMobile}
                textFieldSx={{
                  width: isMobile ? '100%' : '48%',
                }}
                hideClearButton
              />
            </LocalizationProvider>
            <Controller
              name="typeOfServiceId"
              control={control}
              render={({ fieldState, formState, field: { value }, ...props }) => (
                <Autocomplete
                  {...props}
                  sx={{ width: isMobile ? '100%' : '48%' }}
                  data-testid="mui-component-select-type-of-service"
                  options={TypesOfService.sort((a, b) => a.name.localeCompare(b.name))}
                  getOptionLabel={(option: any) => (option.name ? option.name : '')}
                  onChange={handleTypeOfServiceChange}
                  value={
                    value
                      ? TypesOfService.find((typesOfService) => typesOfService.id === value)
                      : null
                  }
                  renderOption={(props, option: any) => {
                    return (
                      <li {...props} key={option.id} value={option.id}>
                        {option.name}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.typeOfServiceId}
                      helperText={errors.typeOfServiceId?.message}
                      margin="normal"
                      fullWidth
                      label={t('timeTracker.items.typeOfService')}
                    />
                  )}
                />
              )}
            />
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              px: isMobile ? 0 : 3,
              pt: 0,
            }}
          >
            <Controller
              name="careTeamMember"
              control={control}
              render={({ fieldState, formState, field: { value }, ...props }) => (
                <Autocomplete
                  {...props}
                  sx={{ width: isMobile ? '100%' : '48%' }}
                  data-testid="mui-component-select-care-team-member"
                  options={careTeamMember}
                  getOptionLabel={(option) => getCareTeamOptionLabel(option as CareTeamItem)}
                  onChange={handleCareTeamMemberChange}
                  value={
                    value
                      ? careTeamMember.find((careTeam) => getCareTeamValue(careTeam) === value)
                      : null
                  }
                  renderOption={(props, option: any) => {
                    const valueKey = getCareTeamValue(option);
                    return (
                      <li {...props} key={valueKey} value={valueKey}>
                        {getCareTeamOptionLabel(option as CareTeamItem)}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.careTeamMember}
                      helperText={errors.careTeamMember?.message}
                      margin="normal"
                      fullWidth
                      label={t('timeTracker.items.careTeamMember')}
                    />
                  )}
                />
              )}
            />
            <Controller
              name="measurement"
              control={control}
              render={({ fieldState, formState, field: { value, onChange }, ...props }) => (
                <Autocomplete
                  {...props}
                  sx={{ width: isMobile ? '100%' : '48%' }}
                  data-testid="mui-component-select-measurement"
                  options={timeTrackerAssessments}
                  getOptionLabel={(option) =>
                    getTimeTrackerAssessmentsOptionLabel(option as TimeTrackerAssessmentItem)
                  }
                  onChange={handleMeasurementChange}
                  value={
                    value
                      ? timeTrackerAssessments.find(
                          (assessment) => getTimeTrackerAssessmentValue(assessment) === value
                        )
                      : null
                  }
                  renderOption={(props, option: any) => {
                    const valueKey = getTimeTrackerAssessmentValue(option);
                    return (
                      <li {...props} key={valueKey} value={valueKey}>
                        {getTimeTrackerAssessmentsOptionLabel(option as TimeTrackerAssessmentItem)}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.measurement}
                      helperText={errors.measurement?.message}
                      margin="normal"
                      fullWidth
                      label={t('timeTracker.items.measurement')}
                    />
                  )}
                />
              )}
            />
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              px: isMobile ? 0 : 3,
              pt: 0,
            }}
          >
            <Controller
              name="minutes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  inputProps={{ 'data-testid': 'minutes-input', min: 1 }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      field.onChange(''); // allow clearing
                    } else {
                      const num = Number(value);
                      field.onChange(num <= 0 ? 1 : num);
                    }
                  }}
                  margin="normal"
                  fullWidth
                  label={t('timeTracker.items.minutes')}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  error={!!errors.minutes}
                  helperText={errors.minutes?.message}
                  sx={{ width: isMobile ? '100%' : '48%' }}
                />
              )}
            />
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              // pb: goalData?.id ? 0 : 3,
              px: isMobile ? 0 : 3,
              pt: 3,
            }}
          >
            {!isMobile && <Divider sx={{ width: '100%' }} />}

            {pageErrors?.length > 0 &&
              pageErrors.map((error) => (
                <Alert key={error} severity="error" sx={{ mt: 2, width: '100%' }}>
                  {error}
                </Alert>
              ))}

            <Button
              type="submit"
              disabled={disableSubmit}
              fullWidth
              variant="contained"
              sx={{
                width: isMobile ? '100%' : '24%',
                my: 2,
                py: 2,
                float: 'right',
              }}
            >
              {isEditing ? t('timeTracker.update') : t('timeTracker.add')}
            </Button>
          </Box>
        </RoundedContainer>
      </Box>
    </>
  );
}
